import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { UserPlus, Phone, Mail, Edit, Trash2, CheckCircle, Sparkles, Loader2, Zap, ArrowRightCircle, BedDouble, Send, UserX, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ConvertToBookingModal from './ConvertToBookingModal';
import axios from 'axios';
import { track, trackFirstTime } from '../../services/analytics';
import { useEmptyStateTracker } from '../../hooks/useEmptyStateTracker';
import { filterBookableRooms, bookableTag } from '../../utils/roomAvailability';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadsList = () => {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [scoringLeadId, setScoringLeadId] = useState(null);
  const [bulkScoring, setBulkScoring] = useState(false);
  const [view, setView] = useState('list'); // list | kanban
  const [draggedId, setDraggedId] = useState(null);
  const [convertingLead, setConvertingLead] = useState(null);
  const [lostLead, setLostLead] = useState(null);
  const navigate = useNavigate();

  // ---- helpers for funnel UX ----
  const isStale = (lead) => {
    // A lead is "stale" if it's stuck in contacted/visited > 3 days with no contact update
    if (!['contacted', 'visited'].includes(lead.status)) return false;
    const ref = lead.last_contacted_at || lead.updated_at || lead.created_at;
    if (!ref) return false;
    const ageHours = (Date.now() - new Date(ref).getTime()) / 36e5;
    return ageHours > 72;
  };
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    property_type: 'PG',
    budget_min: '',
    budget_max: '',
    preferred_location: '',
    move_in_date: '',
    source: 'website',
    status: 'new',
    notes: '',
    follow_up_date: '',
    interested_room_ids: [],
  });
  const [allRooms, setAllRooms] = useState([]);

  const fetchAllRooms = useCallback(async () => {
    try {
      const propsRes = await axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } });
      const all = [];
      for (const p of propsRes.data) {
        try {
          const rRes = await axios.get(`${API}/properties/${p.id}/rooms`, { headers: { Authorization: `Bearer ${token}` } });
          (rRes.data || []).forEach(r => all.push({ ...r, property_name: p.name }));
        } catch { /* ignore */ }
      }
      setAllRooms(all);
    } catch {
      setAllRooms([]);
    }
  }, [token]);

  useEffect(() => { fetchAllRooms(); }, [fetchAllRooms]);

  const fetchLeads = useCallback(async () => {
    try {
      let url = `${API}/leads`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  async function stampContact(leadId) {
    try {
      await axios.post(`${API}/leads/${leadId}/contact`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Logged contact', description: 'Pulled lead to "Contacted" if it was new.' });
      fetchLeads();
    } catch {
      toast({ title: 'Could not stamp contact', variant: 'destructive' });
    }
  }
  function blastRoomsViaWA() {
    // Feature is built but server returns 503 until enabled.
    toast({ title: 'Coming soon', description: 'WhatsApp room-blast launches once Meta approves the template.' });
  }

  useEmptyStateTracker('leads', leads.length, loading);

  function resetForm() {
    setFormData({
      name: '',
      phone: '',
      email: '',
      property_type: 'PG',
      budget_min: '',
      budget_max: '',
      preferred_location: '',
      move_in_date: '',
      source: 'website',
      status: 'new',
      notes: '',
      follow_up_date: '',
      interested_room_ids: [],
    });
  }

  // Strip fields that Pydantic's Optional types reject as empty string
  // (budget_min, budget_max, email). Send null/omit instead of ''.
  const sanitizeLead = (d) => {
    const out = { ...d };
    if (out.email === '') out.email = null;
    if (out.preferred_location === '') out.preferred_location = null;
    if (out.move_in_date === '') out.move_in_date = null;
    if (out.follow_up_date === '') out.follow_up_date = null;
    if (out.notes === '') out.notes = null;
    out.budget_min = out.budget_min === '' || out.budget_min == null ? null : parseInt(out.budget_min, 10);
    out.budget_max = out.budget_max === '' || out.budget_max == null ? null : parseInt(out.budget_max, 10);
    if (!Array.isArray(out.interested_room_ids)) out.interested_room_ids = [];
    return out;
  };

  // Render FastAPI error detail safely — it can be a string OR an array of
  // Pydantic error objects like [{type, loc, msg, input, ctx}]. Never pass
  // raw objects to <toast> or JSX; stringify them to human-readable form.
  const errMsg = (error, fallback = 'Request failed') => {
    const d = error?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      return d
        .map(e => {
          if (typeof e === 'string') return e;
          const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
          return field ? `${field}: ${e.msg}` : e.msg;
        })
        .filter(Boolean)
        .join('; ');
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, sanitizeLead(formData), {
        headers: { Authorization: `Bearer ${token}` }
      });
      track('lead_created', { source: formData.source || 'unknown' });
      trackFirstTime('first_lead_added');
      
      toast({
        title: "Lead Added!",
        description: "New lead has been added successfully"
      });
      
      setShowAddModal(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast({
        title: "Error",
        description: errMsg(error, 'Failed to add lead'),
        variant: "destructive"
      });
    }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      property_type: lead.property_type || 'PG',
      budget_min: lead.budget_min || '',
      budget_max: lead.budget_max || '',
      preferred_location: lead.preferred_location || '',
      move_in_date: lead.move_in_date || '',
      source: lead.source || 'website',
      status: lead.status || 'new',
      notes: lead.notes || '',
      follow_up_date: lead.follow_up_date || '',
      interested_room_ids: lead.interested_room_ids || [],
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/leads/${selectedLead.id}`, sanitizeLead(formData), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Lead Updated!",
        description: "Lead has been updated successfully"
      });
      
      setShowEditModal(false);
      setSelectedLead(null);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast({
        title: "Error",
        description: errMsg(error, 'Failed to update lead'),
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Lead Deleted",
        description: "Lead has been deleted successfully"
      });
      
      fetchLeads();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive"
      });
    }
  };

  const moveLeadToStatus = async (leadId, newStatus) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === newStatus) return;

    // Conversion is NEVER a silent status flip — it must create a booking.
    // If user drags to "converted", open the booking modal instead.
    if (newStatus === 'converted') {
      setConvertingLead(lead);
      toast({
        title: 'Converting to booking',
        description: 'A lead becomes "converted" only after you create its booking.',
      });
      return;
    }

    try {
      await axios.put(`${API}/leads/${leadId}`, { ...lead, status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: `Moved to ${newStatus}` });
      fetchLeads();
    } catch {
      toast({ title: 'Error', description: 'Failed to move lead', variant: 'destructive' });
    }
  };

  const scoreLeadAI = async (leadId) => {
    setScoringLeadId(leadId);
    try {
      const res = await axios.post(`${API}/leads/${leadId}/score`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: `✨ AI Score: ${res.data.score}/100 (${res.data.label})`,
        description: res.data.reasoning
      });
      fetchLeads();
    } catch (err) {
      toast({
        title: "AI Scoring Failed",
        description: err.response?.data?.detail || "Try again",
        variant: "destructive"
      });
    } finally {
      setScoringLeadId(null);
    }
  };

  const bulkScoreLeads = async () => {    setBulkScoring(true);
    try {
      const res = await axios.post(`${API}/leads/bulk-score`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: `✨ Scored ${res.data.scored} leads`,
        description: res.data.errors ? `${res.data.errors} errors` : 'All leads scored successfully'
      });
      fetchLeads();
    } catch (error) {
      toast({
        title: "Bulk scoring failed",
        description: error.response?.data?.detail || "Try again",
        variant: "destructive"
      });
    } finally {
      setBulkScoring(false);
    }
  };

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leads Management</h1>
          <p className="text-gray-600 mt-1">Manage prospective tenants</p>
        </div>
        <Button
          onClick={bulkScoreLeads}
          disabled={bulkScoring}
          data-testid="bulk-score-leads-btn"
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white mr-2"
        >
          {bulkScoring ? <Loader2 className="animate-spin mr-2" size={18} /> : <Zap size={18} className="mr-2" />}
          AI Bulk Score
        </Button>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-lead-btn"
        >
          <UserPlus size={20} className="mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-l-blue-600">
          <p className="text-gray-600 text-sm">Total Leads</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </Card>
        <Card className="p-6 border-l-4 border-l-green-600">
          <p className="text-gray-600 text-sm">New Leads</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.new}</p>
        </Card>
        <Card className="p-6 border-l-4 border-l-yellow-600">
          <p className="text-gray-600 text-sm">Contacted</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.contacted}</p>
        </Card>
        <Card className="p-6 border-l-4 border-l-purple-600">
          <p className="text-gray-600 text-sm">Converted</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.converted}</p>
        </Card>
      </div>

      {/* View Toggle + Filters */}
      <Card className="p-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant={statusFilter === 'all' ? 'default' : 'outline'} onClick={() => setStatusFilter('all')}>All</Button>
          <Button size="sm" variant={statusFilter === 'new' ? 'default' : 'outline'} onClick={() => setStatusFilter('new')}>New</Button>
          <Button size="sm" variant={statusFilter === 'contacted' ? 'default' : 'outline'} onClick={() => setStatusFilter('contacted')}>Contacted</Button>
          <Button size="sm" variant={statusFilter === 'visited' ? 'default' : 'outline'} onClick={() => setStatusFilter('visited')}>Visited</Button>
          <Button size="sm" variant={statusFilter === 'converted' ? 'default' : 'outline'} onClick={() => setStatusFilter('converted')}>Converted</Button>
        </div>
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5 bg-gray-50">
          <button
            data-testid="view-list"
            onClick={() => setView('list')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${view === 'list' ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
          >
            📋 List
          </button>
          <button
            data-testid="view-kanban"
            onClick={() => setView('kanban')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${view === 'kanban' ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
          >
            🗂️ Kanban
          </button>
        </div>
      </Card>

      {/* Kanban View */}
      {view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="leads-kanban">
          {['new', 'contacted', 'visited', 'converted', 'lost'].map(col => {
            const colColor = {
              new:       'border-t-blue-500 bg-blue-50/40',
              contacted: 'border-t-yellow-500 bg-yellow-50/40',
              visited:   'border-t-orange-500 bg-orange-50/40',
              converted: 'border-t-green-500 bg-green-50/40',
              lost:      'border-t-gray-400 bg-gray-50/40',
            }[col];
            // Sort by AI score (hottest first); leads without a score sink to bottom
            const colLeads = leads
              .filter(l => l.status === col)
              .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0));
            return (
              <div
                key={col}
                data-testid={`kanban-col-${col}`}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  if (draggedId) {
                    moveLeadToStatus(draggedId, col);
                    setDraggedId(null);
                  }
                }}
                className={`rounded-xl border border-gray-200 border-t-4 ${colColor} p-3 min-h-[500px]`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 capitalize flex items-center gap-2">
                    {col}
                    <span className="text-xs bg-white text-gray-700 px-2 py-0.5 rounded-full">{colLeads.length}</span>
                  </h3>
                </div>
                <div className="space-y-2">
                  {colLeads.length === 0 ? (
                    <p className="text-xs text-gray-400 italic text-center py-8">Drop leads here</p>
                  ) : colLeads.map(lead => (
                    <div
                      key={lead.id}
                      data-testid={`kanban-card-${lead.id}`}
                      draggable
                      onDragStart={() => setDraggedId(lead.id)}
                      onDragEnd={() => setDraggedId(null)}
                      className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm cursor-move hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold">
                              {lead.name.charAt(0)}
                            </div>
                            {isStale(lead) && (
                              <span
                                title={`Stale — no contact in ${Math.floor((Date.now() - new Date(lead.last_contacted_at || lead.updated_at || lead.created_at).getTime()) / 36e5)}h`}
                                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white animate-pulse"
                                data-testid={`kanban-stale-${lead.id}`}
                              />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">{lead.name}</p>
                            <p className="text-[10px] text-gray-500">{lead.phone}</p>
                          </div>
                        </div>
                        {typeof lead.ai_score === 'number' && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            lead.ai_label === 'hot' ? 'bg-red-100 text-red-700' :
                            lead.ai_label === 'warm' ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-100 text-blue-700'
                          }`} title={lead.ai_reasoning}>
                            ✨{lead.ai_score}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {lead.property_type && <span className="text-[9px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{lead.property_type}</span>}
                        {lead.budget_max && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">≤₹{lead.budget_max.toLocaleString()}</span>}
                        {lead.source && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{lead.source}</span>}
                      </div>
                      {lead.move_in_date && (
                        <p className="text-[10px] text-gray-500">Move-in: {lead.move_in_date}</p>
                      )}
                      {lead.preferred_location && (
                        <p className="text-[10px] text-gray-500 truncate">📍 {lead.preferred_location}</p>
                      )}
                      <div className="mt-2 pt-2 border-t border-gray-100 flex gap-1 justify-end items-center flex-wrap">
                        {lead.status !== 'converted' && lead.status !== 'lost' ? (
                          <button
                            data-testid={`kanban-convert-${lead.id}`}
                            onClick={() => setConvertingLead(lead)}
                            className="text-[10px] font-bold px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-1 mr-auto"
                            title="Convert to Booking"
                          >
                            <ArrowRightCircle size={11} /> Convert
                          </button>
                        ) : lead.status === 'converted' && lead.booking_id ? (
                          <button
                            data-testid={`kanban-view-booking-${lead.id}`}
                            onClick={() => navigate('/manager/people/bookings')}
                            className="text-[10px] font-bold px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200 inline-flex items-center gap-1 mr-auto"
                            title="View linked booking"
                          >
                            <CheckCircle size={11} /> View booking
                          </button>
                        ) : null}

                        {/* Quick contact actions: Call (auto-stamps) + WhatsApp Blast (gated) */}
                        {!['converted', 'lost'].includes(lead.status) && (
                          <>
                            <a
                              href={`tel:+91${lead.phone}`}
                              onClick={() => stampContact(lead.id)}
                              data-testid={`kanban-call-${lead.id}`}
                              className="p-1 text-green-700 hover:bg-green-50 rounded"
                              title="Call now (auto-stamps Last Contacted)"
                            >
                              <Phone size={12} />
                            </a>
                            <button
                              type="button"
                              onClick={blastRoomsViaWA}
                              data-testid={`kanban-blast-${lead.id}`}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded relative cursor-not-allowed"
                              title="Send rooms via WhatsApp — coming soon"
                              disabled
                            >
                              <Send size={12} />
                              <span className="absolute -top-1 -right-1 text-[7px] font-bold bg-gray-100 text-gray-500 px-0.5 rounded">soon</span>
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`kanban-score-${lead.id}`}
                          onClick={() => scoreLeadAI(lead.id)}
                          disabled={scoringLeadId === lead.id}
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                          title="AI score"
                        >
                          {scoringLeadId === lead.id ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
                        </button>
                        {!['converted', 'lost'].includes(lead.status) && (
                          <button
                            data-testid={`kanban-lost-${lead.id}`}
                            onClick={() => setLostLead(lead)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Mark as lost (with reason)"
                          >
                            <UserX size={12} />
                          </button>
                        )}
                        {lead.status === 'lost' && (
                          <button
                            data-testid={`kanban-reactivate-${lead.id}`}
                            onClick={async () => {
                              await axios.post(`${API}/leads/${lead.id}/reactivate`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              toast({ title: 'Lead reactivated', description: 'Moved back to Visited' });
                              fetchLeads();
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Reactivate (move back to Visited)"
                          >
                            <RotateCcw size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(lead)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit size={12} />
                        </button>
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      /* Leads List */
      <div className="grid grid-cols-1 gap-4">
        {leads.length === 0 ? (
          <Card className="p-12 text-center">
            <UserPlus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Leads Found</h3>
            <p className="text-gray-600">Add your first lead to get started</p>
          </Card>
        ) : (
          leads.map((lead) => (
            <Card key={lead.id} className="p-6" data-testid={`lead-card-${lead.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {lead.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{lead.name}</h3>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          lead.status === 'converted' ? 'bg-green-100 text-green-800' :
                          lead.status === 'new' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {lead.status}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                          {lead.property_type}
                        </span>
                        {typeof lead.ai_score === 'number' && (
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 ${
                              lead.ai_label === 'hot' ? 'bg-red-100 text-red-700' :
                              lead.ai_label === 'warm' ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}
                            title={lead.ai_reasoning || ''}
                            data-testid={`lead-ai-score-${lead.id}`}
                          >
                            <Sparkles size={11} /> {lead.ai_score}/100 • {lead.ai_label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={16} />
                      <span>{lead.phone}</span>
                    </div>
                    {lead.email && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={16} />
                        <span>{lead.email}</span>
                      </div>
                    )}
                    {lead.budget_min && (
                      <div className="text-gray-600">
                        <span className="font-semibold">Budget:</span> ₹{lead.budget_min}-{lead.budget_max}
                      </div>
                    )}
                    {lead.follow_up_date && (
                      <div className="text-gray-600">
                        <span className="font-semibold">Follow-up:</span> {lead.follow_up_date}
                      </div>
                    )}
                  </div>
                  {lead.interested_rooms?.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`lead-interested-rooms-${lead.id}`}>
                      <span className="text-xs text-gray-500 flex items-center gap-1"><BedDouble size={12} /> Interested in:</span>
                      {lead.interested_rooms.map(r => (
                        <span key={r.id} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                          {r.property_name} · Rm {r.room_number}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => scoreLeadAI(lead.id)}
                    disabled={scoringLeadId === lead.id}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    data-testid={`lead-ai-score-btn-${lead.id}`}
                    title="AI Score this lead"
                  >
                    {scoringLeadId === lead.id ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                  </Button>
                  {lead.status !== 'converted' && (
                    <Button
                      size="sm"
                      onClick={() => setConvertingLead(lead)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`lead-convert-${lead.id}`}
                    >
                      <ArrowRightCircle size={16} className="mr-1" />
                      Convert to Booking
                    </Button>
                  )}
                  {lead.status === 'converted' && lead.booking_id && (
                    <span
                      className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1"
                      title={`Booking ${lead.booking_id.slice(0,8)}`}
                    >
                      <CheckCircle size={11} /> Booked
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(lead)}
                    className="border-blue-600 text-blue-600"
                    data-testid={`lead-edit-${lead.id}`}
                    title="Edit lead"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(lead.id)}
                    className="border-red-600 text-red-600"
                    data-testid={`lead-delete-${lead.id}`}
                    title="Delete lead"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      )}

      {/* Add Lead Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Property Type</Label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="PG">PG</option>
                  <option value="Hostel">Hostel</option>
                  <option value="Flat">Flat</option>
                  <option value="Studio">Studio</option>
                </select>
              </div>
              <div>
                <Label>Min Budget (₹)</Label>
                <Input
                  type="number"
                  value={formData.budget_min}
                  onChange={(e) => setFormData({...formData, budget_min: e.target.value})}
                />
              </div>
              <div>
                <Label>Max Budget (₹)</Label>
                <Input
                  type="number"
                  value={formData.budget_max}
                  onChange={(e) => setFormData({...formData, budget_max: e.target.value})}
                />
              </div>
              <div>
                <Label>Preferred Location</Label>
                <Input
                  value={formData.preferred_location}
                  onChange={(e) => setFormData({...formData, preferred_location: e.target.value})}
                />
              </div>
              <div>
                <Label>Move-in Date</Label>
                <Input
                  type="date"
                  value={formData.move_in_date}
                  onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Source</Label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="walk_in">Walk-in</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="visited">Visited</option>
                  <option value="negotiating">Negotiating</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Interested Rooms (optional — multi-select)</Label>
                <select
                  data-testid="lead-interested-rooms"
                  multiple
                  value={formData.interested_room_ids}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                    setFormData({ ...formData, interested_room_ids: selected });
                  }}
                  className="w-full px-3 py-2 border rounded-lg h-28"
                >
                  {filterBookableRooms(allRooms).map(r => (
                    <option key={r.id} value={r.id}>
                      {r.property_name} · Room {r.room_number}
                      {r.room_type ? ` · ${r.room_type}` : ''}
                      {` · ₹${r.rent_per_bed || 0}/bed · ${bookableTag(r)}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Showing only rooms that are available or whose notice-period exit has passed. Hold Ctrl/⌘ to pick multiple.</p>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Add Lead
              </Button>
              <Button type="button" onClick={() => setShowAddModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="visited">Visited</option>
                  <option value="negotiating">Negotiating</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Interested Rooms (optional — multi-select)</Label>
                <select
                  data-testid="lead-interested-rooms"
                  multiple
                  value={formData.interested_room_ids}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                    setFormData({ ...formData, interested_room_ids: selected });
                  }}
                  className="w-full px-3 py-2 border rounded-lg h-28"
                >
                  {(() => {
                    // Bookable now + any previously-picked room (preserve existing selections)
                    const bookable = filterBookableRooms(allRooms);
                    const picked = (formData.interested_room_ids || []).filter(
                      id => !bookable.find(r => r.id === id)
                    );
                    const extras = allRooms.filter(r => picked.includes(r.id));
                    return [...bookable, ...extras].map(r => (
                      <option key={r.id} value={r.id}>
                        {r.property_name} · Room {r.room_number}
                        {r.room_type ? ` · ${r.room_type}` : ''}
                        {` · ₹${r.rent_per_bed || 0}/bed · ${bookableTag(r) || (r.status || 'unavailable')}`}
                      </option>
                    ));
                  })()}
                </select>
                <p className="text-xs text-gray-500 mt-1">Showing only available rooms (and ones already linked to this lead). Hold Ctrl/⌘ to pick multiple.</p>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Update Lead
              </Button>
              <Button type="button" onClick={() => setShowEditModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead → Booking conversion */}
      {convertingLead && (
        <ConvertToBookingModal
          open={!!convertingLead}
          onClose={() => setConvertingLead(null)}
          onSuccess={() => { setConvertingLead(null); fetchLeads(); }}
          lead={convertingLead}
          token={token}
        />
      )}

      {/* Mark-as-Lost reason picker */}
      <Dialog open={!!lostLead} onOpenChange={() => setLostLead(null)}>
        <DialogContent className="max-w-md" data-testid="lost-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX size={18} className="text-red-600" /> Mark as Lost
            </DialogTitle>
          </DialogHeader>
          {lostLead && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Why are we losing <b>{lostLead.name}</b>? This helps you spot patterns (price too high, wrong location, etc.).
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'price',          label: '💰 Price too high' },
                  { key: 'location',       label: '📍 Wrong location' },
                  { key: 'unreachable',    label: '📵 Unreachable' },
                  { key: 'not_interested', label: '🙅 Not interested' },
                  { key: 'other',          label: '❓ Other' },
                ].map(r => (
                  <button
                    key={r.key}
                    data-testid={`lost-reason-${r.key}`}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/leads/${lostLead.id}/lost`, { reason: r.key }, { headers: { Authorization: `Bearer ${token}` } });
                        toast({ title: 'Marked as lost', description: `Reason: ${r.label}` });
                        setLostLead(null);
                        fetchLeads();
                      } catch (e) {
                        toast({ title: 'Failed', description: e.response?.data?.detail || 'Save failed', variant: 'destructive' });
                      }
                    }}
                    className="text-left text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsList;